import React, { useState } from 'react'
import EventCard from '../EventCard/EventCard'
import PastEventCard from '../PastEventCard/PastEventCard'
import { ChevronDown, ChevronUp } from 'lucide-react'

const EventGrid = ({ title, events }) => {
  const [showAll, setShowAll] = useState(false);
  const INITIAL_DISPLAY_COUNT = 6;

  // Check if events array is empty or undefined
  if (!events || events.length === 0) {
    return (
      <div className="w-full max-w-[1920px] px-4 sm:px-8 md:px-12 lg:px-16 xl:px-24 py-8">
        {/* Section Title */}
        {title && (
          <div className="mb-6">
            <h2 className="text-3xl sm:text-4xl font-bold text-Color-Black font-['Montserrat']">
              {title}
            </h2>
          </div>
        )}
        
        {/* Empty State */}
        <div className="flex flex-col items-center justify-center py-16 bg-gray-50 rounded-lg">
          <div className="text-6xl mb-4">📅</div>
          <h3 className="text-xl font-semibold text-gray-700 mb-2 font-['Montserrat']">
            No Upcoming Events
          </h3>
          <p className="text-gray-500 text-center max-w-md font-['Montserrat']">
            We're currently planning exciting events for you. Stay tuned for announcements about upcoming webinars, hackathons, and workshops!
          </p>
        </div>
      </div>
    )
  }

  // Determine how many events to display
  const displayedEvents = showAll ? events : events.slice(0, INITIAL_DISPLAY_COUNT);
  const hasMoreEvents = events.length > INITIAL_DISPLAY_COUNT;
  
  // Calculate total number of events for descending numbering
  const totalEvents = events.length;

  return (
    <div className="w-full max-w-[1920px] px-4 sm:px-8 md:px-12 lg:px-16 xl:px-24 py-8">
      {/* Section Title */}
      {title && (
        <div className="mb-6">
          <h2 className="text-3xl sm:text-4xl font-bold text-Color-Black font-['Montserrat']">
            {title}
          </h2>
        </div>
      )}
      
      {/* Grid Container */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 auto-rows-fr">
        {displayedEvents.map((event, index) => {
          // Determine if event is past based on status
          const isPastEvent = event.status === 'completed' || event.status === 'past';
          
          // Calculate descending display number
          const displayNumber = totalEvents - index;
          
          // Determine if this card should animate in
          const isNewCard = index >= INITIAL_DISPLAY_COUNT;
          
          return (
            <div 
              key={event.id} 
              className={`flex transition-all duration-500 ease-in-out ${
                isNewCard && showAll 
                  ? 'animate-fadeInUp opacity-100' 
                  : isNewCard 
                  ? 'hidden' 
                  : 'opacity-100'
              }`}
              style={{
                animationDelay: isNewCard && showAll ? `${(index - INITIAL_DISPLAY_COUNT) * 50}ms` : '0ms'
              }}
            >
              {isPastEvent ? (
                <PastEventCard event={event} displayNumber={displayNumber} />
              ) : (
                <EventCard event={event} displayNumber={displayNumber} />
              )}
            </div>
          );
        })}
      </div>

      {/* Show More/Less Button - Centered at Bottom */}
      {hasMoreEvents && (
        <div className="flex justify-center mt-8">
          <button
            onClick={() => setShowAll(!showAll)}
            className="flex flex-row items-center gap-2 text-gray-500 opacity-50 hover:opacity-100 transition-opacity duration-200 text-base font-semibold font-['Montserrat'] group"
          >
            <span>{showAll ? 'Show Less' : 'Show More'}</span>
            {showAll ? (
              <ChevronUp className="w-5 h-5 group-hover:-translate-y-1 transition-transform duration-200" />
            ) : (
              <ChevronDown className="w-5 h-5 group-hover:translate-y-1 transition-transform duration-200" />
            )}
          </button>
        </div>
      )}

      {/* CSS for fade in up animation */}
      <style jsx>{`
        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        .animate-fadeInUp {
          animation: fadeInUp 0.5s ease-out forwards;
        }
      `}</style>
    </div>
  )
}

export default EventGrid
